import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getDocument } from '../api/document';
import { useSourcePanel } from './SourcePanelContext';
import { ApiError } from '../api/client';
import { useT } from '../I18nContext';

/**
 * Slide-in source panel. Rendered once near the dashboard root;
 * controlled via SourcePanelContext.
 *
 * Side panel (rather than centred modal) so the user can read the
 * source alongside the chat answer. Click outside the panel or
 * press Escape to close.
 *
 * Loads the document via /api/document/<id>. Shows the title +
 * extracted headings (as a "Contents" sidebar) + the full text.
 * Markdown isn't rendered - these are plain extractions, not
 * model output.
 */
export function SourcePanel() {
  const { target, close } = useSourcePanel();
  const t = useT();

  // The slide-in panel is doc-only. Video targets are handled inline
  // by MessageSources / VideoCitationTile, sharing the same target
  // state so opening a video closes any open doc panel and vice
  // versa.
  const docTarget = target?.kind === 'doc' ? target : null;
  const docId = docTarget?.docId ?? '';
  const titleHint = docTarget?.titleHint ?? '';

  const query = useQuery({
    queryKey: ['document', docId],
    queryFn: () => getDocument(docId),
    enabled: !!docId,
    // Document content is mostly static - cache for an hour so
    // re-clicks within a session don't re-fetch.
    staleTime: 60 * 60_000,
    retry: (failureCount, err) => {
      // 403/404 won't get better on retry - surface them immediately.
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        return false;
      }
      return failureCount < 1;
    },
  });

  // Esc to close. Mounted only when there's a doc target to keep
  // the listener footprint minimal.
  useEffect(() => {
    if (!docTarget) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [docTarget, close]);

  if (!docTarget) return null;

  // Prefer fetched title (admin friendly-name overlay may differ
  // from the source array's titleHint). Falls back to hint while
  // the fetch is in flight.
  const title = query.data?.title ?? titleHint ?? t('message.source_doc_default');
  const headings = query.data?.headings ?? [];
  const content = query.data?.content ?? '';
  const sourceFile = query.data?.source_file ?? '';

  return (
    <>
      {/* Scrim - soft dim behind the panel. Click to close. */}
      <div className="source-panel-scrim" onClick={close} aria-hidden />

      <aside
        className="source-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Source: ${title}`}
      >
        <header className="source-panel-header">
          <div className="source-panel-header-text">
            <FileText size={16} className="source-panel-header-icon" aria-hidden />
            <h2 className="source-panel-title">{title}</h2>
          </div>
          <button
            type="button"
            className="source-panel-close"
            onClick={close}
            aria-label={t('source_panel.close')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="source-panel-body">
          {query.isLoading && (
            <p className="source-panel-status">Loading document…</p>
          )}

          {query.isError && (
            <div className="auth-alert auth-alert-danger" role="alert">
              {query.error instanceof ApiError && query.error.status === 403
                ? 'Document citation view is not enabled for this organisation. ' +
                  'Ask an administrator to turn it on in the Documents settings.'
                : 'Unable to load document content. The source may have been ' +
                  'removed or you no longer have access.'}
            </div>
          )}

          {query.isSuccess && (
            <>
              {headings.length > 0 && (
                <div className="source-panel-toc">
                  <div className="source-panel-toc-label">{t('source_panel.contents')}</div>
                  <ul className="source-panel-toc-list">
                    {headings.map((h, i) => (
                      <li key={i}>{h}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Doc body. Extracted text from the source - usually
                  paragraphs separated by blank lines (which renders
                  as paragraphs in markdown) plus the occasional
                  heading the extractor preserved. ReactMarkdown +
                  remark-gfm gives us tables / lists / autolinks
                  for free; on plain prose it just renders <p>s. */}
              <div className="source-panel-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>

              {sourceFile && (
                <div className="source-panel-meta">
                  <span className="source-panel-meta-label">Source:</span>{' '}
                  <span className="source-panel-meta-value">{sourceFile}</span>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
